import { get } from 'api/client';
import {
  AudienceRestrictionFieldOption,
  AudienceRestrictions,
  AudienceRestrictionsOptions,
} from 'app/modules/posts/types/AudienceRestrictionsOptions';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import config from 'config';
import StringUtils from 'utils/string';
import {
  AUDIENCE_RESTRICTIONS_AGES,
  AudienceRestrictionField,
} from 'app/modules/posts/modules/detail/modules/audienceRestrictions/constants/audienceRestrictions';

type SearchOptionsParams = {
  term: string;
  type: AudienceRestrictionField;
};

type PossibleLNAudienceRestrictions = Partial<
  Record<AudienceRestrictionField, AudienceRestrictionFieldOption[]>
>;

let possibleLNAudienceRestrictions: PossibleLNAudienceRestrictions;

function getFBQueryTypeByField(
  type: Exclude<AudienceRestrictionField, 'age_min'>,
) {
  return {
    [AudienceRestrictionField.cities]: 'adcity',
    [AudienceRestrictionField.countries]: 'adcountry',
    [AudienceRestrictionField.regions]: 'adregion',
    [AudienceRestrictionField.locales]: 'adlocale',
    [AudienceRestrictionField.languages]: 'adlanguage',
    [AudienceRestrictionField.fbCountryGroups]: 'adcountry_group',
    [AudienceRestrictionField.lnCountryGroups]: 'adcountry_group',
  }[type];
}

interface AudienceRestrictionPresetItem {
  age: number | null;
  cities: number[];
  country_groups: number[];
  regions: number[];
  countries: number[];
  languages: number[];
  locales: number[];
}

interface AudienceRestrictionPresetDetails {
  [key: string]: AudienceRestrictionPresetItem;
}

export interface AudiencePreset {
  id: number;
  name: string;
  social_network_type: number;
  audience_restrictions: AudienceRestrictionPresetDetails;
}

export const AudienceRestrictionsApi = {
  searchFBOptions(
    params: SearchOptionsParams,
  ): Promise<AudienceRestrictionFieldOption[]> {
    if (params.type === AudienceRestrictionField.ageMin) {
      return Promise.resolve(
        AUDIENCE_RESTRICTIONS_AGES.map((restriction) => ({
          ...restriction,
          id: restriction.value,
        })),
      );
    }

    return get<{ id: number; label: string }[]>(
      '/Pages/ajaxRetrieveFacebookAdApiData',
      {
        params: {
          q: params.term,
          dataType: getFBQueryTypeByField(params.type),
        },
      },
    ).then((res) =>
      res.map((item) => ({
        label: item.label,
        value: item.id,
      })),
    );
  },
  async searchLNOptions(
    params: SearchOptionsParams,
  ): Promise<AudienceRestrictionFieldOption[]> {
    const possibilities = await this.getPossibleLNOptions();

    const fieldAudienceRestrictions = possibilities[params.type];

    if (!fieldAudienceRestrictions) {
      return [];
    }

    if (!params.term) {
      return Promise.resolve(fieldAudienceRestrictions);
    }

    return Promise.resolve(
      fieldAudienceRestrictions.filter((item) =>
        StringUtils.isSubstring(params.term, item.label),
      ),
    );
  },
  searchOptions(
    params: SearchOptionsParams & { pageType: number },
  ): Promise<AudienceRestrictionFieldOption[]> {
    switch (params.pageType) {
      case TYPE.FACEBOOK:
        return AudienceRestrictionsApi.searchFBOptions(params);
      case TYPE.LINKEDIN:
        return AudienceRestrictionsApi.searchLNOptions(params);
      default:
        return Promise.resolve([]);
    }
  },
  async getLNOptionsByOptionIds(
    values: AudienceRestrictions,
  ): Promise<AudienceRestrictionsOptions> {
    const possibleFieldOptions = await this.getPossibleLNOptions();

    const fields = Object.fromEntries(
      Object.entries(values).map(([field, values]) => {
        const indexedPossibilities = Object.fromEntries(
          possibleFieldOptions[field as AudienceRestrictionField]?.map(
            (option) => [option.value, option],
          ) ?? [],
        );

        const options: AudienceRestrictionFieldOption[] = [];

        const valuesArr = Array.isArray(values) ? values : [values];

        valuesArr.forEach((id) => {
          if (indexedPossibilities[id]) {
            options.push(indexedPossibilities[id]);
          }
        });

        return [field, options];
      }),
    );

    return Promise.resolve(fields);
  },
  async getFBOptionsByOptionsIds(
    values: AudienceRestrictions,
  ): Promise<AudienceRestrictionsOptions> {
    const fields: Partial<
      Record<AudienceRestrictionField, AudienceRestrictionFieldOption[]>
    > = Object.fromEntries(Object.keys(values).map((field) => [field, []]));

    await Promise.all(
      Object.entries(values).map(([field, options]) => {
        return this.getPossibleFBOptions({
          items: Array.isArray(options) ? options : [options],
          type: field as AudienceRestrictionField,
        }).then((options) => {
          const indexedPossibilities = Object.fromEntries(
            options.map((option) => [option.value, option]),
          );

          const fieldValues = values[field as AudienceRestrictionField];

          const valuesArr = Array.isArray(fieldValues)
            ? fieldValues
            : [fieldValues];

          valuesArr?.forEach((value) => {
            if (value && indexedPossibilities[value]) {
              fields[field as AudienceRestrictionField]?.push(
                indexedPossibilities[value],
              );
            }
          });
        });
      }),
    );

    return Promise.resolve(fields);
  },
  async getOptionsByOptionIds(
    values: AudienceRestrictions,
    pageType: number,
  ): Promise<AudienceRestrictionsOptions> {
    if (Object.keys(values).length === 0) {
      return {};
    }

    switch (pageType) {
      case TYPE.LINKEDIN:
        return this.getLNOptionsByOptionIds(values);
      case TYPE.FACEBOOK:
        return this.getFBOptionsByOptionsIds(values);
      default:
        return Promise.resolve({});
    }
  },
  async getPossibleFBOptions(params: {
    type: AudienceRestrictionField;
    items: (number | string)[];
  }): Promise<AudienceRestrictionFieldOption[]> {
    if (params.items.length === 0) {
      return [];
    }

    if (params.type === AudienceRestrictionField.ageMin) {
      return Promise.resolve(
        AUDIENCE_RESTRICTIONS_AGES.map((restriction) => ({
          ...restriction,
          id: restriction.value,
        })),
      );
    }

    return get<{ id: string; label: string }[]>(
      'Pages/ajaxRetrieveFbAdContentItems',
      {
        params: {
          type: getFBQueryTypeByField(params.type),
          items: JSON.stringify(params.items),
        },
      },
    ).then((res) =>
      res.map((item) => ({
        label: item.label,
        value: item.id,
      })),
    );
  },

  async getPossibleLNOptions(): Promise<PossibleLNAudienceRestrictions> {
    function getAudienceRestriction(response: Record<string, string>) {
      return Object.entries(response).map(([key, value]) => ({
        value: key,
        label: value,
      }));
    }

    if (!possibleLNAudienceRestrictions) {
      const { data_fields } = await get<{
        data_fields: {
          countries: Record<string, string>;
          countryGroups: Record<string, string>;
          languages: Record<string, string>;
          locales: Record<string, string>;
        };
      }>('/audience-restrictions/data-fields/linkedin', {
        baseURL: config.API_SERVICE_ENDPOINT,
      });

      possibleLNAudienceRestrictions = {
        locales: getAudienceRestriction(data_fields.locales),
        countryGroups: getAudienceRestriction(data_fields.countryGroups),
        countries: getAudienceRestriction(data_fields.countries),
        languages: getAudienceRestriction(data_fields.languages),
      };
    }

    return Promise.resolve(possibleLNAudienceRestrictions);
  },

  async getAudienceSize(
    values: AudienceRestrictions,
    pageId: number,
  ): Promise<{ size: number; status: boolean }> {
    const countries = values.countries || [];
    const countryGroups = values.countryGroups || [];
    const languages = values.languages || [];

    return get<{ size: number; status: boolean }>(
      '/pages/ajaxRetrieveLinkedInAudienceSize',
      {
        params: {
          pageId,
          countries: JSON.stringify(countries),
          countryGroups: JSON.stringify(countryGroups),
          languages: JSON.stringify(languages),
        },
      },
    );
  },
  getPresets(pageType: number): Promise<AudiencePreset[]> {
    return get<AudiencePreset[]>(
      'AudienceRestrictionsPresets/ajaxGetPresetsForSocialNetwork',
      {
        params: {
          network: pageType,
        },
      },
    );
  },
};

export default AudienceRestrictionsApi;
